import React, {useState, forwardRef, useRef, useEffect} from 'react';
import {Input, Icon, Button, Portal, Dropdown} from 'semantic-ui-react';

type OperatorValue =
    | {operator: '=' | '>' | '<'}
    | {operator: 'BETWEEN'; start: string; end: string};

interface DateFilterProps {
    field?: string;
    onNewFilterChange?: (field: string, value: object) => void;
    t: any;
}

const OperatorDropdownPortal = ({value, onChange, t}: any) => {
    const operatorOptions = [
        {key: 'eq', value: 'EQ', text: t('GLOBAL:EQUAL')},
        {key: 'gt', value: 'GT', text: t('GLOBAL:GREATER_THAN')},
        {key: 'lt', value: 'LT', text: '<'},
        {key: 'range', value: 'BETWEEN', text: t('GLOBAL:IN_RANGE')}, // Updated to use BETWEEN
    ];

    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLSpanElement>(null);

    // Extract operator, start, end
    let operator = '=';
    let start = '';
    let end = '';
    if (value && typeof value === 'object' && 'operator' in value) {
        operator = value.operator;
        if (operator === 'BETWEEN') { // Updated to check for BETWEEN
            start = (value as any).start || '';
            end = (value as any).end || '';
        }
    }

    // Find the selected option's text
    let buttonText = '=';
    for (let i = 0; i < operatorOptions.length; i++) {
        if (operatorOptions[i].value === operator) {
            buttonText = operatorOptions[i].text;
            break;
        }
    }

    // Calculate position for the portal menu
    let menuStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 2000,
        minWidth: 260,
        background: '#fff',
        padding: 10,
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    };
    if (buttonRef.current) {
        // @ts-ignore
        const rect = (buttonRef.current as HTMLElement).getBoundingClientRect();
        menuStyle.top = rect.bottom + window.scrollY;
        menuStyle.left = rect.left + window.scrollX;
    }

    function handleOperatorChange(e: any, data: any) {
        if (data.value === 'BETWEEN') { // Updated to handle BETWEEN
            onChange({operator: 'BETWEEN', start: '', end: ''});
        } else {
            onChange({operator: data.value});
            setOpen(false);
        }
    }

    function handleRangeChange(type: 'start' | 'end', date: string) {
        onChange({
            operator: 'BETWEEN', // Updated to use BETWEEN
            start: type === 'start' ? date : start,
            end: type === 'end' ? date : end,
        });
    }

    function handleApplyRange() {
        setOpen(false);
    }

    // Close portal on scroll
    useEffect(
        () => {
            function handleScroll() {
                if (open) setOpen(false);
            }
            if (open) {
                window.addEventListener('scroll', handleScroll, true); // true = capture phase, catches scroll on all ancestors
            }
            return () => {
                window.removeEventListener('scroll', handleScroll, true);
            };
        },
        [open]
    );

    return (
        <>
            <span ref={buttonRef} style={{display: 'inline-block'}}>
                <Button
                    content={buttonText}
                    onClick={function () {
                        setOpen(!open);
                    }}
                    size="small"
                />
                {/* Add a hidden Dropdown for accessibility if needed, but keep open={false} */}
                <Dropdown style={{display: 'none'}} open={false} />
            </span>
            <Portal
                open={open}
                onClose={function () {
                    setOpen(false);
                }}
                closeOnDocumentClick={true}
            >
                <div style={menuStyle as React.CSSProperties}>
                    <Dropdown
                        selection={true}
                        search={true}
                        options={operatorOptions}
                        value={operator}
                        onChange={handleOperatorChange}
                        open={true} // Only this one is open
                        placeholder="Select operator"
                        style={{width: '100%', marginBottom: operator === 'BETWEEN' ? 10 : 0}} // Updated to check for BETWEEN
                    />
                    {operator === 'BETWEEN' && ( // Updated to check for BETWEEN
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <div>
                                <label style={{fontWeight: 500, marginRight: 4}}>
                                    {t('GLOBAL:DATE_FORMAT')}:
                                </label>
                                <input
                                    type="date"
                                    value={start}
                                    onChange={function (e: any) {
                                        handleRangeChange('start', e.target.value);
                                    }}
                                    style={{marginRight: 8}}
                                    placeholder={t('GLOBAL:DATE_FORMAT')}
                                />
                            </div>
                            <div>
                                <label style={{fontWeight: 500, marginRight: 4}}>
                                    {t('GLOBAL:END_DATE')}:
                                </label>
                                <input
                                    type="date"
                                    value={end}
                                    onChange={function (e: any) {
                                        handleRangeChange('end', e.target.value);
                                    }}
                                    placeholder={t('GLOBAL:END_DATE')}
                                />
                            </div>
                            <Button
                                content={t('GLOBAL:APPLY')}
                                size="tiny"
                                onClick={handleApplyRange}
                                disabled={!start || !end}
                                style={{marginLeft: 8}}
                                primary={true}
                            />
                        </div>
                    )}
                </div>
            </Portal>
        </>
    );
};

interface DateInputWithIconsProps {
    operator: string; // 'range' or other
    value: string;
    startDate: string;
    endDate: string;
    onChange: (val: string) => void;
    onStartDateChange: (val: string) => void;
    onEndDateChange: (val: string) => void;
    onClear: () => void;
    onStartClear: () => void;
    onEndClear: () => void;
    onSearch: () => void;
    placeholder?: string;
    t: any;
}

const today = new Date().toISOString().split('T')[0];

const DateInputWithIcons: React.FC<DateInputWithIconsProps> = ({
    operator,
    value,
    startDate,
    endDate,
    onChange,
    onStartDateChange,
    onEndDateChange,
    onClear,
    onStartClear,
    onEndClear,
    onSearch,
    placeholder,
    t
}) => {
    const isRange = operator === 'BETWEEN';
    const canSearch = isRange
        ? (startDate === '' && endDate === '') // enable if both empty
        || (startDate !== '' && endDate !== '') // enable if both filled
        : true;

    if (isRange) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: 4,
                    width: '100%',
                    background: '#fff',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
            >
                <Input
                    type="date"
                    fluid={true}
                    style={{width: '100%'}}
                    placeholder={t('GLOBAL:DATE_FORMAT')}
                    value={startDate}
                    onChange={(e, data) => onStartDateChange(data.value)}
                    icon={
                        startDate ? (
                            <Icon
                                name="close"
                                link={true}
                                onClick={onStartClear}
                                title={t('GLOBAL:CLEAR')}
                                style={{cursor: 'pointer', color: '#888'}}
                            />
                        ) : null
                    }
                />
                <Input
                    type="date"
                    fluid={true}
                    style={{width: '100%'}}
                    placeholder={t('GLOBAL:END_DATE')}
                    value={endDate}
                    max={today}
                    onChange={(e, data) => onEndDateChange(data.value)}
                    icon={
                        endDate ? (
                            <Icon
                                name="close"
                                link={true}
                                onClick={onEndClear}
                                title={t('GLOBAL:CLEAR')}
                                style={{cursor: 'pointer', color: '#888'}}
                            />
                        ) : null
                    }
                />
                <Icon
                    name="search"
                    link={canSearch}
                    onClick={canSearch ? onSearch : undefined}
                    title={t('GLOBAL:SEARCH')}
                    style={{
                        marginLeft: 8,
                        cursor: canSearch ? 'pointer' : 'not-allowed',
                        color: canSearch ? '#888' : '#ccc',
                        opacity: canSearch ? 1 : 0.5
                    }}
                    disabled={!canSearch}
                />
            </div>
        );
    }

    // Single date mode
    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                background: '#fff',
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                padding: 4,
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <Input
                type="date"
                fluid={true}
                style={{width: '100%'}}
                placeholder={placeholder || t('GLOBAL:DATE_FORMAT')}
                value={value}
                max={today}
                onChange={(e, data) => onChange(data.value)}
                icon={
                    <span style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        {value && (
                            <Icon
                                name="close"
                                link={true}
                                onClick={onClear}
                                title={t('GLOBAL:CLEAR')}
                                style={{cursor: 'pointer', color: '#888'}}
                            />
                        )}
                        <Icon
                            name="search"
                            link={true}
                            onClick={onSearch}
                            title={t('GLOBAL:SEARCH')}
                            style={{cursor: 'pointer', color: '#888'}}
                        />
                    </span>}
            />
        </div>
    );
};

const DateFilter = forwardRef((props: DateFilterProps, ref) => {
    const {field, onNewFilterChange, t} = props;
    const [operatorValue, setOperatorValue] = useState<any>({operator: 'EQ'});
    const [dateValue, setDateValue] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // When operator changes
    function handleOperatorChange(val: OperatorValue) {
        setOperatorValue(val);
        if (val.operator === 'BETWEEN') {
            setStartDate(val.start || '');
            setEndDate(val.end || '');
        } else {
            setDateValue('');
        }
    }

    function handleDateChange(val: string) {
        setDateValue(val);
    }

    function handleStartDateChange(val: string) {
        setStartDate(val);
        setOperatorValue({
            operator: 'BETWEEN',
            start: val,
            end: endDate
        });
    }

    function handleEndDateChange(val: string) {
        setEndDate(val);
        setOperatorValue({
            operator: 'BETWEEN',
            start: startDate,
            end: val
        });
    }

    function formatDateDDMMYY(dateStr: string): string {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }

    function clearDate() {
        setDateValue('');
        triggerFilter('');
        setOperatorValue({operator: 'EQ'});
    }

    function triggerFilter(overrideDateValue?: string) {
        if (onNewFilterChange && field) {
            if (operatorValue.operator === 'BETWEEN') {
                onNewFilterChange(field, {
                    startDate: formatDateDDMMYY(startDate),
                    endDate: formatDateDDMMYY(endDate),
                    operator: operatorValue.operator
                });
            } else {
                if (overrideDateValue === undefined || overrideDateValue === '') {
                    onNewFilterChange(field, {
                        operator: operatorValue.operator,
                        startDate: null
                    });
                } else {
                    onNewFilterChange(field, {
                        operator: operatorValue.operator,
                        startDate: formatDateDDMMYY(dateValue)
                    });
                }
            }
        }
    }

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            triggerFilter();
        }
    };

    function clearStartDate() {
        setStartDate('');
        setOperatorValue({
            operator: 'BETWEEN',
            start: '',
            end: endDate
        });
    }

    function clearEndDate() {
        setEndDate('');
        setOperatorValue({
            operator: 'BETWEEN',
            start: startDate,
            end: ''
        });
    }

    return (
        <div
            className="date-filter-container"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px',
                width: '100%',
            }}
        >
            <OperatorDropdownPortal value={operatorValue} onChange={handleOperatorChange} t={t} />
            {operatorValue.operator === 'BETWEEN' ? (
                <>
                    <DateInputWithIcons
                        operator="BETWEEN"
                        placeholder={t('GLOBAL:DATE_FORMAT')}
                        value={startDate}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(val: any) => handleStartDateChange(val)}
                        onStartDateChange={handleStartDateChange}
                        onEndDateChange={handleEndDateChange}
                        onClear={clearStartDate}
                        onStartClear={clearStartDate}
                        onEndClear={clearEndDate}
                        onSearch={triggerFilter}
                        t={t}
                    />
                </>
            ) : (
                <DateInputWithIcons
                    operator="single"
                    placeholder={t('GLOBAL:DATE_FORMAT')}
                    value={dateValue}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(val: any) => handleDateChange(val)}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onClear={clearDate}
                    onStartClear={clearStartDate}
                    onEndClear={clearEndDate}
                    onSearch={triggerFilter}
                    t={t}
                />
            )}
        </div>
    );
});

export default DateFilter;



// WEBPACK FOOTER //
// ./src/components/AgGridFilter/DateFilter.tsx